import {
  CalendarOutlined,
  PushpinOutlined,
  PushpinTwoTone,
  TagOutlined,
} from '@ant-design/icons';
import { Icon } from '@iconify/react';
import { ConfigProvider, Steps, Timeline as AntdTimeline } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import PrimaryLogo from 'assets/brand/primary-logo.svg';
import fourthLogo from 'assets/icons/brands/fourth.svg';
import netsuiteIcon from 'assets/icons/brands/netsuite.svg?url';
import { useState, useRef, useEffect } from 'react';
import { colors } from 'theme/colors';

import { ChatInput } from 'pages/maxi-ai/ChatInput';
import type { ChatInputHandle } from 'pages/maxi-ai/ChatInput';

import { AiDisclaimer } from 'components/ai/AiDisclaimer';
import { Input } from 'components/data-entry/Input';
import { MessageContent } from 'components/maxi-ai/MessageContent';
import { Tag } from 'components/tag/Tag';

import { useMaxiAI } from 'hooks/maxi-ai/useMaxiAI';
import { useThreads } from 'hooks/maxi-ai/useThreads';

import { cn } from 'utils/cn';
import { featureFlags } from 'utils/config/featureFlags';
import { Logger } from 'utils/logger';

import { useAuthStore } from 'store/index';
import { useMaxiAIStore } from 'store/maxi-ai.store';

import type { Source } from 'types/maxi-ai';

const SUGGESTED_QUESTIONS = [
  'What is driving the change in sales compared with the same week last year?',
  'How is lunch performing compared with the same period last year?',
  'What is the change in food sales this period?',
  'What is the change in wine sales this period?',
  'What is the breakdown of complimentary items?',
  'What is the change in covers across locations?',
];

// Loading animation for bot messages
function BotLoadingAnimation() {
  return (
    <div className="flex space-x-1">
      {[0, 0.1, 0.2].map((delay, i) => (
        <div
          key={i}
          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
          style={{ animationDelay: `${delay}s` }}
        />
      ))}
    </div>
  );
}

// Thought process toggle header
function ThoughtProcessHeader({
  thoughtProcess,
  isExpanded,
  onToggle,
}: {
  thoughtProcess: any;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const isInProgress = thoughtProcess.currentThought || !thoughtProcess.end;

  return (
    <div
      className="flex gap-1 items-center cursor-pointer pb-2"
      onClick={onToggle}
    >
      {isInProgress ? (
        <>
          <div className="text-base text-color-text-secondary">Thinking...</div>
        </>
      ) : (
        <>
          <div className="text-base">Steps</div>
          <Icon
            icon="radix-icons:caret-right"
            className={cn(
              'text-xl transition-transform duration-200',
              isExpanded ? '-rotate-90' : 'rotate-0'
            )}
          />
        </>
      )}
    </div>
  );
}

// Azumi POC: Steps component for thought process display
function AzumiThoughtSteps() {
  return (
    <ConfigProvider
      theme={{
        components: {
          Steps: {
            colorPrimary: colors.smartGrey4,
            colorSplit: colors.smartGrey4,
          },
        },
      }}
    >
      <Steps
        direction="vertical"
        progressDot
        size="small"
        className="text-sm [&_.ant-steps-item]:!mb-0 [&_.ant-steps-item-content]:!min-h-0"
        items={[
          {
            title: 'Search data',
            status: 'finish',
            description: (
              <div className="flex gap-1 mt-1">
                <Tag
                  icon={
                    <img
                      src={fourthLogo}
                      alt="Fourth"
                      className="w-3.5 h-3.5 inline-block"
                    />
                  }
                  label="Fourth"
                />
              </div>
            ),
          },
          {
            title: 'Applied filters',
            status: 'finish',
            description: (
              <div className="flex gap-1 mt-1">
                <Tag icon={<CalendarOutlined />} label="Dec 28, 2025" />
                <Tag icon={<TagOutlined />} label="Food" />
              </div>
            ),
          },
          {
            title: 'Analyzed data',
            status: 'finish',
            description: (
              <div className="text-color-text-secondary mt-1">
                Successfully compiled an analysis based on available data
                sources
              </div>
            ),
          },
        ]}
      />
    </ConfigProvider>
  );
}

function ChatWidget() {
  const user = useAuthStore((state) => state.user);
  const { FF__ASK_MAXI_AI_BUTTON } = featureFlags;

  const [expandedThoughts, setExpandedThoughts] = useState<Set<string>>(
    new Set()
  );
  const [isEditingThreadTitle, setIsEditingThreadTitle] = useState(false);
  const [editedThreadTitle, setEditedThreadTitle] = useState('');

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<TextAreaRef>(null);
  const messagesEnd = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<ChatInputHandle>(null);

  const { messages, isMockMode } = useMaxiAIStore();
  const { sendMessage, isLoading, error } = useMaxiAI();
  const { threadId, currentThread, updateThreadTitle, updateThreadPinned } =
    useThreads();

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Handle chat errors
  useEffect(() => {
    if (error) {
      Logger.error('Chat hook error:', error);
      // Error is already handled by the hook, this is just for debugging
    }
  }, [error]);

  // Toggle thought process expansion
  const toggleThoughtProcess = (thoughtId: string) => {
    setExpandedThoughts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(thoughtId)) {
        newSet.delete(thoughtId);
      } else {
        newSet.add(thoughtId);
      }
      return newSet;
    });
  };

  // Send message with validation
  const handleSendMessage = (
    message: string,
    currentWorkspace: string,
    selectedSources: Source[]
  ) => {
    const isValidRequest =
      message.trim() &&
      !isLoading &&
      (FF__ASK_MAXI_AI_BUTTON || selectedSources.length > 0);

    if (!isValidRequest) return;

    sendMessage(message, currentWorkspace, selectedSources);
  };

  const handleSuggestedQuestion = (question: string): void => {
    if (isMockMode) {
      sendMessage(question, '', []);
      return;
    }
    handleSendMessage(question, '', []);
  };

  // Get current thread title
  const currentThreadTitle = currentThread?.title;

  const handleEditThreadTitle = () => {
    setIsEditingThreadTitle(true);
    setEditedThreadTitle(currentThreadTitle || '');
  };

  const handleSaveThreadTitle = async () => {
    if (
      !threadId ||
      !editedThreadTitle.trim() ||
      editedThreadTitle === currentThreadTitle
    ) {
      setIsEditingThreadTitle(false);
      return;
    }

    try {
      await updateThreadTitle(threadId, editedThreadTitle.trim());
      setIsEditingThreadTitle(false);
    } catch (error) {
      Logger.error('Failed to update thread title:', error);
    }
  };

  const handlePinThread = async () => {
    if (!threadId) return;
    await updateThreadPinned(threadId, !currentThread?.pinned);
  };

  return (
    <div className="w-full h-full flex flex-col rounded-2xl bg-white">
      {/* Header section - shows thread title or welcome message */}
      {messages.length === 0 && (
        <div className="flex gap-2 items-center p-4">
          <div className="w-8 h-8 bg-chart-color-lime rounded-full flex items-center justify-center">
            <img src={PrimaryLogo} alt="Maximor" className="w-4 h-4" />
          </div>
          <div className="text-base font-medium">
            {currentThreadTitle || (
              <>
                What would you like to ask
                {user ? `, ${user.displayName?.split(' ')[0] || ''}?` : '?'}
              </>
            )}
          </div>
        </div>
      )}

      {/* Thread title header when there are messages */}
      {messages.length > 0 && currentThreadTitle && (
        <div className="flex gap-2 items-center justify-between p-4 pb-2">
          {isEditingThreadTitle ? (
            <div className="flex gap-2 items-center max-w-full">
              <Input
                value={editedThreadTitle}
                className="text-base font-medium min-w-72 max-w-full py-1.5"
                onChange={(e) => setEditedThreadTitle(e.target.value)}
              />
              <div
                className="text-primary text-sm cursor-pointer"
                onClick={handleSaveThreadTitle}
              >
                Save
              </div>
            </div>
          ) : (
            <div className="flex gap-2 items-center">
              <div className="text-base font-medium truncate">
                {currentThreadTitle}
              </div>
              <Icon
                icon="ant-design:edit-outlined"
                className="w-5 h-5 text-lightgray-7 cursor-pointer"
                onClick={handleEditThreadTitle}
              />
            </div>
          )}
          {currentThread?.pinned ? (
            <PushpinTwoTone
              className="w-5 h-5 cursor-pointer"
              twoToneColor={colors.colorPrimary}
              onClick={handlePinThread}
            />
          ) : (
            <PushpinOutlined
              className="w-5 h-5 cursor-pointer"
              color={colors.colorPrimary}
              onClick={handlePinThread}
            />
          )}
        </div>
      )}

      {/* Chat messages */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 pb-1 flex flex-col justify-start min-h-0 scroll-smooth"
        style={{
          maxHeight: 'calc(100% - 50px)',
          scrollBehavior: 'smooth',
        }}
      >
        <div className="flex flex-col max-xl:gap-2 overflow-y-auto scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {messages.map((message, i) => (
            <div
              key={message.id}
              className={cn(
                'flex items-start gap-2',
                message.type === 'CHAT_MESSAGE' && message.isUser
                  ? 'justify-end'
                  : 'justify-start'
              )}
            >
              {message.type === 'CHAT_MESSAGE' ? (
                <div
                  className={cn(
                    'py-2 break-words',
                    message.isUser
                      ? 'px-3 max-w-[80%] bg-lightgray-1 rounded-md'
                      : 'w-full text-gray-900'
                  )}
                >
                  {message.isUser ? (
                    /* User message - plain text */
                    <div>{message.text}</div>
                  ) : /* Bot message - show loading or markdown */
                  !message.text && isLoading && i === messages.length - 1 ? (
                    <BotLoadingAnimation />
                  ) : (
                    <MessageContent
                      className={cn(
                        'text-base [&_ol]:pl-4 [&_ul]:pl-4 [&_li]:ml-4 [&_strong]:font-extrabold',
                        !featureFlags.FF__AZUMI_POC &&
                          '[&_strong]:text-color-primary'
                      )}
                      message={message}
                    />
                  )}
                </div>
              ) : (
                /* Thought process display */
                <div className="flex gap-2 justify-end">
                  <div className="flex flex-col gap-2">
                    <ThoughtProcessHeader
                      thoughtProcess={message}
                      isExpanded={expandedThoughts.has(message.id)}
                      onToggle={() => toggleThoughtProcess(message.id)}
                    />

                    {/* Expandable thought timeline */}
                    <div
                      className={cn(
                        'transition-all duration-300 ease-in-out origin-top',
                        expandedThoughts.has(message.id)
                          ? 'opacity-100 transform scale-y-100'
                          : 'opacity-0 transform scale-y-0'
                      )}
                    >
                      {expandedThoughts.has(message.id) &&
                        (featureFlags.FF__AZUMI_POC ? (
                          <AzumiThoughtSteps />
                        ) : (
                          <AntdTimeline
                            className="text-sm text-color-text-secondary [&_.ant-timeline-item]:!pb-1.5 [&_.ant-timeline-item-tail]:!border-lightgray-4"
                            items={message.completeThoughts.map((thought) => ({
                              children: (
                                <div className="max-w-[80%] break-words">
                                  <div className="font-medium">
                                    {thought.thoughtText}
                                  </div>
                                  {thought.description && (
                                    <div>{thought.description}</div>
                                  )}
                                  {thought.urls?.length > 0 && (
                                    <div className="flex gap-2 my-1">
                                      {thought.urls.map((url) => (
                                        <a
                                          className="p-1 rounded-3xl outline-1 outline-primarygreen2 bg-primarygreen1"
                                          href={url}
                                          key={url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <img
                                            src={netsuiteIcon}
                                            alt="NetSuite"
                                            className="w-4 h-4"
                                          />
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ),
                              dot: (
                                <Icon
                                  icon="mdi:circle"
                                  className="w-2 h-2 text-color-text-secondary"
                                />
                              ),
                            }))}
                          />
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          {!isLoading && isMockMode && (
            <div className="mt-4 flex flex-wrap gap-2">
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => handleSuggestedQuestion(q)}
                  className="text-xs bg-white border border-gray-200 hover:border-primary hover:text-primary px-3 py-1.5 rounded-full transition-colors text-left max-w-full"
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          <div ref={messagesEnd} />
        </div>
      </div>

      {/* AI Disclaimer */}
      <AiDisclaimer className="px-4 py-2 justify-center" />

      {/* Input section */}
      <ChatInput
        ref={chatInputRef}
        isLoading={isLoading}
        onSendMessage={handleSendMessage}
        inputRef={inputRef}
      />
    </div>
  );
}

ChatWidget.displayName = 'ChatWidget';

export { ChatWidget };
